'use client';

import { c as _c } from "react/compiler-runtime";
import { Info, Loader2 } from 'lucide-react';
import { GlassGhostButton, GlassMetadataPill } from '@/shared/ui/glass';

/**
 * What the page is not showing, and the one button that shows more of it.
 *
 * THE BOUND IS SAID OUT LOUD, NEVER RAISED (plan ruling 12). The tray reads one
 * `NOTIFICATIONS_PAGE` page, and until now the centre simply ended: a broker
 * with 412 lines saw the newest page of them and nothing on the surface said
 * the rest existed. The server says it stopped at the bound and how many lines
 * the set holds, both from the statement that returned the page, so this line
 * is read rather than counted here.
 *
 * IT IS WORDS AND ONE FIGURE. The count is what is missing, not a dashboard: a
 * sentence, the number, and the action. The rows behind it arrive by asking for
 * the next page, which is what the cursor on the contract has been there for.
 *
 * Nothing is drawn when the server did not stop, because a "load older" button
 * on a complete archive is a question with no answer behind it.
 */
export const LIST_CEILING_SENTENCE = 'This history is as long as it goes here. Narrow the lens to see the rest.';
export function OlderDays(t0) {
  const $ = _c(12);
  const {
    truncated,
    shown,
    total,
    hasOlder,
    atCeiling,
    loading,
    onLoadOlder
  } = t0;
  if (!truncated && !hasOlder && !atCeiling) {
    return null;
  }
  const remaining = total === null ? null : Math.max(total - shown, 0);
  const t1 = remaining === null ? "There are older days this page has not loaded." : `${String(remaining)} older ${remaining === 1 ? "line is" : "lines are"} not on this page yet.`;
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="min-w-0 text-[12px] text-[#44546F] dark:text-white/70">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hasOlder || $[3] !== loading || $[4] !== onLoadOlder) {
    t3 = hasOlder ? <GlassGhostButton type="button" onClick={onLoadOlder} disabled={loading}>{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}{loading ? "Loading" : "Load older days"}</GlassGhostButton> : null;
    $[2] = hasOlder;
    $[3] = loading;
    $[4] = onLoadOlder;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== atCeiling) {
    t4 = atCeiling ? <div className="flex w-full justify-center"><GlassMetadataPill className="items-start gap-1.5 rounded-2xl px-3 py-1 text-[11px] text-gray-600 dark:text-gray-300"><Info aria-hidden={true} className="mt-0.5 size-3.5 shrink-0" /><span>{LIST_CEILING_SENTENCE}</span></GlassMetadataPill></div> : null;
    $[6] = atCeiling;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white px-4 py-3 dark:bg-white/12">{t2}{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}